import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Star } from 'lucide-react';
import { EmptyState, Field, Section, Select, Spinner } from '@zt/ui';
import { api, ApiError } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { usePermissions } from '../lib/session';
import type { BusinessProfileView, Ga4Report } from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';

/**
 * Vitrin → Google raporları (F6-18, ADR-099): mağazaya bağlanan GA4 mülkünün kanal ve gün raporu (Data API) ve
 * Google Business Profile konum bilgisi ile son yorumlar. İkisi de salt okuma ve istek anında Google'dan çekilir.
 */
const DAYS = ['7', '28', '90'] as const;

export function GoogleReportsPage() {
  const { can } = usePermissions();
  const { stores, storeId, setStoreId } = useStoreChoice();
  return (
    <PageFrame helpKey="page.google_reports">
      <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
      {storeId && can('tracking.read') && <Ga4Section key={`ga4-${storeId}`} storeId={storeId} />}
      {storeId && can('seo.read') && <BusinessSection key={`gbp-${storeId}`} storeId={storeId} />}
    </PageFrame>
  );
}

/** Bağlantı veya izin eksikse ne yapılacağını söyleyen boş durum; diğer hatalarda API'nin açıklaması. */
function GoogleProblem({ error }: { error: unknown }) {
  const t = useT();
  const code = error instanceof ApiError ? error.code : null;
  if (code === 'google_not_linked' || code === 'google_scope_missing' || code === 'google_not_connected')
    return (
      <EmptyState
        title={t(`admin.googleReports.problem_${code}`)}
        description={t('admin.googleReports.problemLead')}
        action={
          <Link to="/google" className="text-pine underline underline-offset-2 hover:no-underline">
            {t('admin.googleReports.openConnections')}
          </Link>
        }
      />
    );
  return (
    <p role="alert" className="text-sm text-rose">
      {error instanceof ApiError ? error.title : String(error)}
    </p>
  );
}

function Ga4Section({ storeId }: { storeId: string }) {
  const t = useT();
  const fmt = useMoney();
  const { locale } = useI18n();
  const [days, setDays] = useState<(typeof DAYS)[number]>('28');
  const n = new Intl.NumberFormat(locale === 'en' ? 'en-GB' : 'tr-TR');
  const q = useQuery({
    queryKey: ['ga4-report', storeId, days],
    queryFn: () => api.get<Ga4Report>(`/v1/admin/google/analytics-report?storeId=${storeId}&days=${days}`),
    retry: false,
  });
  const r = q.data;
  const sum = (k: 'sessions' | 'keyEvents' | 'revenueMinor') =>
    r ? r.channels.reduce((a, c) => a + c[k], 0) : 0;
  return (
    <Section
      title={t('admin.googleReports.ga4Title')}
      description={
        r ? t('admin.googleReports.ga4Lead', { property: r.property }) : t('admin.googleReports.ga4LeadEmpty')
      }
      action={<HelpTip k="google_reports.ga4" />}
    >
      <div className="mb-4 max-w-xs">
        <Field label={t('admin.googleReports.period')} help={<HelpTip k="google_reports.period" />}>
          {(p) => (
            <Select {...p} value={days} onChange={(e) => setDays(e.target.value as typeof days)}>
              {DAYS.map((d) => (
                <option key={d} value={d}>
                  {t('admin.googleReports.lastDays', { n: Number(d) })}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      {q.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : q.error ? (
        <GoogleProblem error={q.error} />
      ) : r && r.channels.length === 0 ? (
        <p className="text-sm text-ink-2">{t('admin.googleReports.noData')}</p>
      ) : r ? (
        <div className="space-y-6">
          <dl className="grid gap-3 sm:grid-cols-3" aria-label={t('admin.googleReports.totals')}>
            {[
              { label: t('admin.googleReports.sessions'), value: n.format(sum('sessions')) },
              { label: t('admin.googleReports.keyEvents'), value: n.format(sum('keyEvents')) },
              { label: t('admin.googleReports.revenue'), value: fmt(sum('revenueMinor'), r.currency) },
            ].map((x) => (
              <div key={x.label} className="rounded-md border border-line bg-surface px-3 py-2">
                <dt className="text-xs text-ink-3">{x.label}</dt>
                <dd className="mt-0.5 text-[0.9375rem] font-medium tabular-nums">{x.value}</dd>
              </div>
            ))}
          </dl>
          {(
            [
              ['channels', t('admin.googleReports.byChannel'), t('admin.googleReports.channel')],
              ['daily', t('admin.googleReports.byDay'), t('admin.googleReports.day')],
            ] as const
          ).map(([k, caption, first]) => (
            <div key={k} className="overflow-x-auto rounded-md border border-line">
              <table className="w-full text-sm" aria-label={caption}>
                <caption className="px-3 pt-2 text-start font-medium">{caption}</caption>
                <thead className="text-ink-2">
                  <tr>
                    <th className="px-3 py-2 text-start font-medium">{first}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('admin.googleReports.sessions')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('admin.googleReports.users')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('admin.googleReports.keyEvents')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('admin.googleReports.revenue')}</th>
                  </tr>
                </thead>
                <tbody>
                  {r[k].map((row) => (
                    <tr key={row.key} className="border-t border-line">
                      <td className="px-3 py-2">{row.key}</td>
                      <td className="px-3 py-2 text-end tabular-nums">{n.format(row.sessions)}</td>
                      <td className="px-3 py-2 text-end tabular-nums">{n.format(row.users)}</td>
                      <td className="px-3 py-2 text-end tabular-nums">{n.format(row.keyEvents)}</td>
                      <td className="px-3 py-2 text-end tabular-nums">{fmt(row.revenueMinor, r.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          <p className="text-sm text-ink-3">{t('admin.googleReports.ga4Note')}</p>
        </div>
      ) : null}
    </Section>
  );
}

function BusinessSection({ storeId }: { storeId: string }) {
  const t = useT();
  const { formatDate, locale } = useI18n();
  const one = new Intl.NumberFormat(locale === 'en' ? 'en-GB' : 'tr-TR', { minimumFractionDigits: 1 });
  const q = useQuery({
    queryKey: ['business-profile', storeId],
    queryFn: () => api.get<BusinessProfileView>(`/v1/admin/google/business-profile?storeId=${storeId}`),
    retry: false,
  });
  const b = q.data;
  return (
    <Section
      title={t('admin.googleReports.gbpTitle')}
      description={t('admin.googleReports.gbpLead')}
      action={<HelpTip k="google_reports.business_profile" />}
    >
      {q.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : q.error ? (
        <GoogleProblem error={q.error} />
      ) : b ? (
        <div className="space-y-5">
          <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-[minmax(9rem,auto)_1fr]">
            <dt className="text-ink-3">{t('admin.googleReports.location')}</dt>
            <dd>{b.title ?? b.location}</dd>
            {b.address && (
              <>
                <dt className="text-ink-3">{t('admin.googleReports.address')}</dt>
                <dd>{b.address}</dd>
              </>
            )}
            {b.phone && (
              <>
                <dt className="text-ink-3">{t('admin.googleReports.phone')}</dt>
                <dd>{b.phone}</dd>
              </>
            )}
            {b.website && (
              <>
                <dt className="text-ink-3">{t('admin.googleReports.website')}</dt>
                <dd>{b.website}</dd>
              </>
            )}
            <dt className="text-ink-3">{t('admin.googleReports.rating')}</dt>
            <dd>
              {b.averageRatingTenths === null
                ? '—'
                : t('admin.googleReports.ratingValue', {
                    avg: one.format(b.averageRatingTenths / 10),
                    count: b.totalReviews,
                  })}
            </dd>
          </dl>
          {b.mapsUrl && (
            <p className="text-sm">
              <a
                href={b.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-pine underline underline-offset-2 hover:no-underline"
              >
                {t('admin.googleReports.replyOnGoogle')}
              </a>{' '}
              <HelpTip k="google_reports.reply" />
            </p>
          )}
          {b.reviews.length === 0 ? (
            <p className="text-sm text-ink-2">{t('admin.googleReports.noReviews')}</p>
          ) : (
            <ul className="space-y-3" aria-label={t('admin.googleReports.reviews')}>
              {b.reviews.map((r) => (
                <li key={r.id} className="rounded-md border border-line bg-surface p-3 text-sm">
                  <p className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{r.reviewer ?? t('admin.googleReports.anonymous')}</span>
                    {r.rating !== null && (
                      <span
                        className="inline-flex items-center gap-0.5 text-amber"
                        aria-label={t('admin.googleReports.stars', { n: r.rating })}
                        role="img"
                      >
                        {Array.from({ length: r.rating }, (_, i) => (
                          <Star key={i} className="h-3.5 w-3.5 fill-current" aria-hidden />
                        ))}
                      </span>
                    )}
                    {r.createdAt && <span className="text-ink-3">{formatDate(r.createdAt)}</span>}
                  </p>
                  {r.comment && <p className="mt-1 whitespace-pre-line">{r.comment}</p>}
                  <p className="mt-1 text-ink-2">
                    {r.reply
                      ? t('admin.googleReports.replied', { reply: r.reply })
                      : t('admin.googleReports.noReply')}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </Section>
  );
}
